/**
 * @fileoverview Post Validator - validates all the content of a post and indicates any errors
 *
 * @author Samantha Landen (slanden@yext.com)
 */
goog.module('yext.social.post.PostValidator');
goog.module.declareLegacyNamespace();

const yext = goog.require('yext');
const array = goog.require('goog.array');
const Partner = goog.require('yext.social.post.Partner');

/**
 * @constructor
 * @param {yext.social.post.PostFieldConfiguration} fieldConfiguration
 */
const PostValidator = function(fieldConfiguration) {
  this.fieldConfiguration = fieldConfiguration;
};

/**
 * @const
 * @type {string}
 */
PostValidator.HTTPS = 'https://';

/**
 * @const
 * @type {string}
 */
PostValidator.HTTP = 'http://';

/**
 * Validates the post, optionally based on the data that has changed
 *
 * @param {!Object<string, ?>} post        the entire post data model
 * @param {!Object<string, ?>=} opt_changed the change that triggered the validation
 * @return {string} error if any is present
 */
PostValidator.prototype.validate = function(post) {
  if ((post['message'].length == 0 && !post['asset'] && !post['hyperlink']['url'])
    // google requires non-empty message
    || (array.contains(post['partnerIds'], 715) && post['message'].length == 0)) {
    return yext.msg('Start typing to get started.');
  }

  if (post['partnerIds'].length == 0) {
    return yext.msg('Select at least one partner to post on');
  }

  if (this.calculateRemainingCharacters(post) < 0) {
    return yext.msg(
      'Max character length reached: {0}',
      this.fieldConfiguration.getEffectiveMaxCharLimit(post));
  }

  var minCharLimit = this.fieldConfiguration.getEffectiveMinCharLimit();
  if (minCharLimit > 0 && post['message'].length < minCharLimit) {
    return yext.msg(
      'Updates must contain at least {0} characters on: {1}',
      minCharLimit,
      this.fieldConfiguration.getPartnersWithMinCharLimit(minCharLimit).map(function(partner) {
        return partner.get('name');
      }).join(', '));
  }

  return null;
};

/**
 * @param {!Object<string, ?>} post
 * @return {number} characters remaining before hitting the max character limit
 */
PostValidator.prototype.calculateRemainingCharacters = function(post) {
  var message = post['message'] || '';
  var count = this.fieldConfiguration.getEffectiveMaxCharLimit(post) - message.length;
  var url = post['hyperlink']['url'];

  if (url) {
    var tester = url;
    if (tester.indexOf(PostValidator.HTTPS) == 0) {
      tester = tester.substring(PostValidator.HTTPS.length);
    } else if (tester.indexOf(PostValidator.HTTP) == 0) {
      tester = tester.substring(PostValidator.HTTP.length);
    }

    if (message.indexOf(tester) == -1) {
      count -= url.length;
      if (message.length) {
        count--;
      }
    }
  }

  // Twitter converts all urls in the message to short links with format t.co that use 23 characters
  // Reference: https://help.twitter.com/en/using-twitter/how-to-tweet-a-link
  if (post['partnerIds']
      && post['partnerIds'].length == 1
      && array.contains(post['partnerIds'], yext.social.partners.TWITTER_ID)) {
    var words = message.split(' ');
    for (var i = 0; i < words.length; i++) {
      var word = words[i];
      if (yext.isValidUrl(word)) {
        count += word.length;
        count -= 23;
      }
    }
  }

  return count;
};

/**
 * @param {!Object<string, ?>} post
 * @return {boolean} true if the post has an event or call to action and not all selected partners
 *                   support those fields
 */
PostValidator.prototype.hasPartialEventAndCTASupport = function(post) {
  return (
    (post['callToAction'] != null
     && !this.fieldConfiguration.isFieldAllowed(Partner.PostField.CALL_TO_ACTION))
    || (post['event'] != null
     && !this.fieldConfiguration.isFieldAllowed(Partner.PostField.EVENTS)));
};

/**
 * @param {!Object<string, ?>} post
 * @return {string|undefined} error
 */
PostValidator.prototype.doPublisherSpecificValidation = function(post) {
  if (array.contains(post['partnerIds'], 715)) { // GoogleMyBusiness
    let fileSizeError = yext.msg(
      'To post on Google Local, your photo must be between 10KB and 5MB in size' +
      ' and at least 250x250 pixels');
    let minFileSize = 10240;
    let maxFileSize = 5242880;
    if (post['photo'] != null
      && (post['photo']['size'] < minFileSize || post['photo']['size'] > maxFileSize
          || post['photo']['width'] < 250 || post['photo']['height'] < 250)) {
      return fileSizeError;
    }
    if (post['asset'] && post['asset']['file']) {
      let fileSize = post['asset']['file']['size'];
      if (fileSize && (fileSize < minFileSize || fileSize > maxFileSize)) {
        return fileSizeError;
      }
    }
  } else if (array.contains(post['partnerIds'], 559)) { // Facebook
    if (post['message'] != null && post['message'].includes('facebook.com')) {
      return yext.msg('Facebook post text may not contain a Facebook URL');
    }
  }

  return undefined;
};

exports = PostValidator;
